import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, isRef as _isRef, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, vModelDynamic as _vModelDynamic, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = { class: "relative min-h-[100dvh] w-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800" }
const _hoisted_2 = { class: "w-full min-h-[100dvh] flex flex-col md:flex-row bg-white dark:bg-gray-800 overflow-hidden" }
const _hoisted_3 = { class: "hidden md:flex w-1/2 bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 items-center justify-center p-8" }
const _hoisted_4 = { class: "text-center text-white space-y-6" }
const _hoisted_5 = { class: "text-3xl font-bold" }
const _hoisted_6 = { class: "flex flex-col justify-center items-center w-full md:w-1/2 p-4 sm:p-8 lg:p-12 relative flex-1 md:flex-none overflow-hidden" }
const _hoisted_7 = { class: "flex md:hidden flex-col items-center justify-center pt-4 pb-8 px-4 shrink-0" }
const _hoisted_8 = ["src"]
const _hoisted_9 = { class: "w-full max-w-md space-y-4 md:space-y-8" }
const _hoisted_10 = { class: "bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700" }
const _hoisted_11 = {
  key: 0,
  class: "text-2xl sm:text-3xl font-extrabold text-primary dark:text-primary-dark-text mb-6 text-center"
}
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { class: "flex justify-between items-center mb-1" }
const _hoisted_14 = { class: "flex justify-center gap-2 mt-4" }
const _hoisted_15 = ["onUpdate:modelValue", "onInput", "onKeydown", "disabled"]
const _hoisted_16 = {
  key: 0,
  class: "text-sm text-center text-red-500 font-medium"
}
const _hoisted_17 = ["disabled"]
const _hoisted_18 = { class: "relative" }
const _hoisted_19 = ["type"]
const _hoisted_20 = { class: "relative" }
const _hoisted_21 = ["type"]
const _hoisted_22 = ["disabled"]
const _hoisted_23 = {
  key: 4,
  class: "flex flex-col items-center justify-center text-center h-full space-y-2"
}

import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useThemeStore } from '@/stores/theme'
import { useTitle } from '@/composables/ui/useTitle'
import { useForgotPassword } from '@/composables/auth/useForgotPassword'
import { Sun, Moon, LoaderCircle, Eye, EyeOff } from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'ForgotPasswordView',
  setup(__props) {

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'PLANET Home Healthcare')

const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)

useTitle(`${appTitle.value} - Forgot Password`)

const {
	email,
	otp,
	newPassword,
	confirmPassword,
	step,
	loading,
	timeLeft,
	sendOtp,
	verifyOtp,
	changePassword
} = useForgotPassword()

const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

// ─── OTP BOX LOGIC ───
const otpDigits = ref(['', '', '', '', '', ''])
const otpInputsRef = ref<HTMLInputElement[]>([])

const onOtpInput = (index: number, event: Event) => {
	const target = event.target as HTMLInputElement
	let val = target.value

	// Only allow numbers
	if (!/^\d*$/.test(val)) {
		otpDigits.value[index] = ''
		val = ''
	}

	// Move to next input if filled
	if (val && index < 5) {
		otpInputsRef.value[index + 1]?.focus()
	}

	otp.value = otpDigits.value.join('')
}

const onOtpKeyDown = (index: number, event: KeyboardEvent) => {
	// Handle Arrow Keys for traversing
	if (event.key === 'ArrowLeft' && index > 0) {
		event.preventDefault()
		otpInputsRef.value[index - 1]?.focus()
	} else if (event.key === 'ArrowRight' && index < 5) {
		event.preventDefault()
		otpInputsRef.value[index + 1]?.focus()
	}
	
	// Handle Backspace
	else if (event.key === 'Backspace') {
		if (!otpDigits.value[index] && index > 0) {
			// If current is empty, move back and clear the previous one
			otpDigits.value[index - 1] = ''
			otpInputsRef.value[index - 1]?.focus()
			otp.value = otpDigits.value.join('')
		} else {
			// If current is NOT empty, ensure sync
			setTimeout(() => {
				otp.value = otpDigits.value.join('')
			}, 0)
		}
	}
	
	// Handle Delete key
	else if (event.key === 'Delete') {
		otpDigits.value[index] = ''
		otp.value = otpDigits.value.join('')
	}
}

const onOtpPaste = (event: ClipboardEvent) => {
	event.preventDefault()
	const pastedData = event.clipboardData?.getData('text/plain')
	if (!pastedData) return
	
	const numbers = pastedData.replace(/\D/g, '').slice(0, 6)
	numbers.split('').forEach((num, i) => {
		if (i < 6) otpDigits.value[i] = num
	})
	
	otp.value = otpDigits.value.join('')
	
	// Focus last filled input or next empty
	const nextEmptyIndex = otpDigits.value.findIndex(d => !d)
	const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex
	otpInputsRef.value[focusIndex]?.focus()
}

// ─── PREVENT REFRESH ON STEP 2 & 3 ───
const handleBeforeUnload = (e: BeforeUnloadEvent) => {
	if (step.value === 2 || step.value === 3) {
		e.preventDefault()
		e.returnValue = '' // Required for Chrome to show the prompt
	}
}

onMounted(() => {
	window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
	window.removeEventListener('beforeunload', handleBeforeUnload)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(themeStore).toggleTheme())),
      class: "md:hidden absolute top-4 right-4 p-2 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition z-10",
      "aria-label": "Toggle dark mode"
    }, [
      (isDarkMode.value)
        ? (_openBlock(), _createBlock(_unref(Sun), {
            key: 0,
            "h-6": "",
            "w-6": ""
          }))
        : (_openBlock(), _createBlock(_unref(Moon), {
            key: 1,
            "h-6": "",
            "w-6": ""
          }))
    ]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[11] || (_cache[11] = _createElementVNode("img", {
            src: "images/main-logo-dark.png",
            alt: "App Logo",
            class: "mx-auto h-24 w-[18rem] object-contain drop-shadow-md"
          }, null, -1)),
          _createElementVNode("h2", _hoisted_5, _toDisplayString(appTitle.value), 1)
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("img", {
            src: isDarkMode.value ? 'images/main-logo-dark.png' : 'images/main-logo.png',
            alt: "App Logo",
            class: "h-16 w-auto max-w-[18rem] object-contain drop-shadow-sm"
          }, null, 8, _hoisted_8)
        ]),
        _createElementVNode("button", {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(themeStore).toggleTheme())),
          class: "hidden md:block absolute top-6 right-6 p-2 rounded-full text-slate-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition",
          "aria-label": "Toggle dark mode"
        }, [
          (isDarkMode.value)
            ? (_openBlock(), _createBlock(_unref(Sun), {
                key: 0,
                "h-6": "",
                "w-6": ""
              }))
            : (_openBlock(), _createBlock(_unref(Moon), {
                key: 1,
                "h-6": "",
                "w-6": ""
              }))
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            (_unref(step) !== 4)
              ? (_openBlock(), _createElementBlock("h2", _hoisted_11, _toDisplayString(_unref(step) === 1 ? 'Forgot Your Password?' : _unref(step) === 2 ? 'Verify Code' : 'Reset Your Password'), 1))
              : _createCommentVNode("", true),
            (_unref(step) === 1)
              ? (_openBlock(), _createElementBlock("form", {
                  key: 1,
                  class: "space-y-6",
                  onSubmit: _cache[3] || (_cache[3] = _withModifiers(
//@ts-ignore
(...args) => (_unref(sendOtp) && _unref(sendOtp)(...args)), ["prevent"]))
                }, [
                  _createElementVNode("div", null, [
                    _cache[12] || (_cache[12] = _createElementVNode("label", {
                      for: "email",
                      class: "label"
                    }, "Email address", -1)),
                    _withDirectives(_createElementVNode("input", {
                      id: "email",
                      type: "email",
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(email) ? (email).value = $event : null)),
                      required: "",
                      class: "input w-full",
                      placeholder: "Enter your email address"
                    }, null, 512), [
                      [_vModelText, _unref(email)]
                    ])
                  ]),
                  _createElementVNode("button", {
                    disabled: !_unref(email) || _unref(loading),
                    type: "submit",
                    class: "w-full flex justify-center py-3 bg-primary text-white font-semibold rounded-lg shadow-md transition hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  }, [
                    (_unref(loading))
                      ? (_openBlock(), _createBlock(_unref(LoaderCircle), {
                          key: 0,
                          class: "w-5 h-5 mr-2 animate-spin"
                        }))
                      : _createCommentVNode("", true),
                    _createTextVNode(" " + _toDisplayString(_unref(loading) ? 'Sending OTP...' : 'Send OTP'), 1)
                  ], 8, _hoisted_12),
                  _cache[13] || (_cache[13] = _createElementVNode("a", {
                    class: "flex justify-center font-bold text-primary hover:text-primary-hover dark:text-secondary-dark-text dark:hover:text-secondary-dark-hover hover:underline",
                    href: "/portal/login"
                  }, " Back to login ", -1))
                ], 32))
              : (_unref(step) === 2)
                ? (_openBlock(), _createElementBlock("form", {
                    key: 2,
                    class: "space-y-6",
                    onSubmit: _cache[5] || (_cache[5] = _withModifiers(
//@ts-ignore
(...args) => (_unref(verifyOtp) && _unref(verifyOtp)(...args)), ["prevent"]))
                  }, [
                    _createElementVNode("div", null, [
                      _createElementVNode("div", _hoisted_13, [
                        _cache[14] || (_cache[14] = _createElementVNode("label", {
                          for: "otp",
                          class: "label mb-0"
                        }, "Enter OTP", -1)),
                        (_openBlock(), _createElementBlock("span", {
                          key: _unref(timeLeft),
                          class: _normalizeClass(["text-sm font-medium inline-block", _unref(timeLeft) > 0 ? 'text-primary dark:text-primary-light' : 'text-red-500'])
                        }, _toDisplayString(_unref(timeLeft) > 0 ? `${Math.floor(_unref(timeLeft) / 60)}:${(_unref(timeLeft) % 60).toString().padStart(2, '0')}` : 'Expired'), 3))
                      ]),
                      _createElementVNode("div", _hoisted_14, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(otpDigits.value, (digit, index) => {
                          return _withDirectives((_openBlock(), _createElementBlock("input", {
                            key: index,
                            ref_for: true,
                            ref_key: "otpInputsRef",
                            ref: otpInputsRef,
                            type: "text",
                            inputmode: "numeric",
                            maxlength: "1",
                            "onUpdate:modelValue": ($event: any) => ((otpDigits.value[index]) = $event),
                            onInput: ($event: any) => (onOtpInput(index, $event)),
                            onKeydown: ($event: any) => (onOtpKeyDown(index, $event)),
                            onPaste: onOtpPaste,
                            disabled: _unref(timeLeft) === 0 || _unref(loading),
                            class: "w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-0 focus:border-primary dark:focus:border-primary-light disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-900 transition-colors"
                          }, null, 40, _hoisted_15)), [
                            [_vModelText, otpDigits.value[index]]
                          ])
                        }), 128))
                      ])
                    ]),
                    (_unref(timeLeft) === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                          _cache[15] || (_cache[15] = _createTextVNode(" Your OTP has expired. ", -1)),
                          _createElementVNode("a", {
                            href: "#",
                            onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (step.value = 1), ["prevent"])),
                            class: "text-primary hover:underline"
                          }, "Click here to request a new one"),
                          _cache[16] || (_cache[16] = _createTextVNode(". ", -1))
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("button", {
                      type: "submit",
                      disabled: _unref(loading) || _unref(timeLeft) === 0,
                      class: "w-full py-3 bg-gradient-to-br from-primary to-primary-hover/50 hover:bg-secondary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg shadow-md transition"
                    }, _toDisplayString(_unref(loading) ? 'Verifying OTP...' : 'Verify OTP'), 9, _hoisted_17)
                  ], 32))
                : (_unref(step) === 3)
                  ? (_openBlock(), _createElementBlock("form", {
                      key: 3,
                      class: "space-y-6",
                      onSubmit: _cache[10] || (_cache[10] = _withModifiers(
//@ts-ignore
(...args) => (_unref(changePassword) && _unref(changePassword)(...args)), ["prevent"]))
                    }, [
                      _createElementVNode("div", null, [
                        _cache[17] || (_cache[17] = _createElementVNode("label", {
                          for: "new-password",
                          class: "label"
                        }, "New Password", -1)),
                        _createElementVNode("div", _hoisted_18, [
                          _withDirectives(_createElementVNode("input", {
                            id: "new-password",
                            type: showNewPassword.value ? 'text' : 'password',
                            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(newPassword) ? (newPassword).value = $event : null)),
                            class: "w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-300 outline-none pr-10",
                            placeholder: "********"
                          }, null, 8, _hoisted_19), [
                            [_vModelDynamic, _unref(newPassword)]
                          ]),
                          _createElementVNode("button", {
                            type: "button",
                            onClick: _cache[7] || (_cache[7] = ($event: any) => (showNewPassword.value = !showNewPassword.value)),
                            class: "absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          }, [
                            (_openBlock(), _createBlock(_resolveDynamicComponent(showNewPassword.value ? _unref(EyeOff) : _unref(Eye)), { class: "h-5 w-5" }))
                          ])
                        ])
                      ]),
                      _createElementVNode("div", null, [
                        _cache[18] || (_cache[18] = _createElementVNode("label", {
                          for: "confirm-password",
                          class: "label"
                        }, "Confirm Password", -1)),
                        _createElementVNode("div", _hoisted_20, [
                          _withDirectives(_createElementVNode("input", {
                            id: "confirm-password",
                            type: showConfirmPassword.value ? 'text' : 'password',
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => (_isRef(confirmPassword) ? (confirmPassword).value = $event : null)),
                            class: "w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-300 outline-none pr-10",
                            placeholder: "********"
                          }, null, 8, _hoisted_21), [
                            [_vModelDynamic, _unref(confirmPassword)]
                          ]),
                          _createElementVNode("button", {
                            type: "button",
                            onClick: _cache[9] || (_cache[9] = ($event: any) => (showConfirmPassword.value = !showConfirmPassword.value)),
                            class: "absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          }, [
                            (_openBlock(), _createBlock(_resolveDynamicComponent(showConfirmPassword.value ? _unref(EyeOff) : _unref(Eye)), { class: "h-5 w-5" }))
                          ])
                        ])
                      ]),
                      _createElementVNode("button", {
                        type: "submit",
                        disabled: _unref(loading),
                        class: "w-full py-3 bg-gradient-to-r from-primary to-primary-hover/50 hover:bg-secondary hover:opacity-90 text-white font-semibold rounded-lg shadow-md transition"
                      }, _toDisplayString(_unref(loading) ? 'Resetting...' : 'Reset Password'), 9, _hoisted_22)
                    ], 32))
                  : (_openBlock(), _createElementBlock("div", _hoisted_23, [...(_cache[19] || (_cache[19] = [
                      _createElementVNode("h3", { class: "text-xl font-semibold text-green-600" }, " Password Reset Successful ", -1),
                      _createElementVNode("p", { class: "text-slate-600 dark:text-slate-400" }, [
                        _createTextVNode(" You can now "),
                        _createElementVNode("a", {
                          href: "/portal/login",
                          class: "text-primary hover:text-primary-hover dark:text-secondary-dark-text dark:hover:text-secondary-dark-hover font-bold hover:underline"
                        }, "log in"),
                        _createTextVNode(" with your new password. ")
                      ], -1)
                    ]))]))
          ])
        ])
      ])
    ])
  ]))
}
}

})